import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Box, Button, Stack, Typography } from '@mui/material'
import { affexyColors as C } from '@/theme/affexyTokens'
import type { RespondentStudy } from '@/services/respondentService'
import { youTubeId } from '@/utils/youtube'
import { enterFullscreen, lockOrientationToCurrent } from '@/utils/fullscreen'
import {
  CALIBRATION_VARIANT_SHAPES,
  DEFAULT_CALIBRATION_VARIANT,
  type HeadPose,
} from '@/lib/calibrationVariants'
import PoseGuide from '@/components/gaze/PoseGuide'
import { GazeTracker, type GazeFrame } from '@/lib/gaze/gazeTracker'
import {
  EMPTY_POSE_VIEW,
  POSE_TOLERANCE_DEG,
  PoseHold,
  headFromBase,
  poseOffset,
  within,
  type HeadDeg,
  type PoseView,
} from '@/lib/gaze/headPose'

/** One logged dot, in TWO coordinate systems on purpose.
 *
 *  `x`/`y` are CONTENT-normalised (0..1 inside the stimulus content rectangle)
 *  — the system the server-side affine fit and the research-panel overlay have
 *  always used. Unchanged, so that path keeps working.
 *
 *  `viewport_x`/`viewport_y` are the same dot as a fraction of the VIEWPORT.
 *  The gaze container answers in viewport pixels and needs its calibration
 *  targets in that system; deriving them later is impossible, because the
 *  content rectangle depends on the stimulus aspect ratio measured at
 *  recording time and is not stored anywhere else.
 *
 *  `stage` splits the dots the engine fits on from the dots it scores itself
 *  against — without the second kind there is no accuracy number at all. */
export type CalibrationPoint = {
  x: number
  y: number
  viewport_x: number
  viewport_y: number
  start_ms: number
  end_ms: number
  pose: HeadPose
  stage: 'calibration' | 'validation'
}

// Per dot: how long each target is shown before advancing. This used to be
// split into a "settle" delay the client stripped off the logged window and a
// "dwell" window that was actually reported — but the future gaze container
// does its own settle handling from the raw shown/hidden timestamps
// (protokol-gaze-kontener.md: "calibration.settle_ms liczy kontener, nie
// FaceWise"), so trimming here would cut the window twice. We now log the
// dot's *entire* visible window and leave settle-stripping to whoever
// consumes it downstream (today: nobody — the server-side affine fit already
// averages over the whole window and tolerates the extra frames).
const SETTLE_MS = 600
const DWELL_MS = 1400
const POINT_MS = SETTLE_MS + DWELL_MS
// Pause between head poses so the respondent has time to actually turn their
// head before the next batch of points starts. Budget per
// FaceWise-brain/20-produkt/warianty-kalibracji.md ("~3 s na ustawienie każdej
// nowej pozy głowy").
const POSE_INTRO_MS = 3000
// Head-pose guidance (same rule as the admin lab): MediaPipe Face Landmarker
// measures the head, a pose starts only once the head holds the target, and a
// dot that the head drifts out of is thrown away and shown again. Only
// MediaPipe runs here — no gaze model; gaze is computed server-side.
//
// A MediaPipe load that never resolves must not trap the respondent on the
// intro screen (it did once — see gaze-respondent-browser-pipeline): after this
// long we fall back to the old timed run without guidance.
const TRACKER_LOAD_TIMEOUT_MS = 15000
// A pose the respondent can't reach (stiff neck, odd camera angle) gets a way
// out: after this long on the guidance screen, or after this many drifts out of
// the same pose, a "continue without guidance" button appears.
const SKIP_AFTER_MS = 15000
const SKIP_AFTER_DRIFTS = 4
// Face out of frame this long while a dot runs → back to the guidance screen.
const FACE_LOST_MS = 1000
// If a video/url aspect can't be measured quickly, assume 16:9.
const ASPECT_TIMEOUT_MS = 3500
const DEFAULT_VIDEO_ASPECT = 16 / 9

// Shown before the closing validation pass. Deliberately does NOT say "test" or
// "sprawdzian" — the respondent is not being graded, the tracker is.
const VALIDATION_COPY = {
  title: 'Ostatnia seria kropek',
  hint: 'Patrz prosto w ekran i powiódź wzrokiem za każdą kropką.',
}

const POSE_COPY: Record<HeadPose, { title: string; hint: string }> = {
  center: { title: 'Głowa prosto', hint: 'Patrz przed siebie, głowa nieruchoma.' },
  left: { title: 'Odwróć głowę w lewo', hint: 'Delikatnie, wzrok cały czas na kropce.' },
  right: { title: 'Odwróć głowę w prawo', hint: 'Delikatnie, wzrok cały czas na kropce.' },
  top: { title: 'Unieś głowę lekko do góry', hint: 'Delikatnie, wzrok cały czas na kropce.' },
  bottom: { title: 'Opuść głowę lekko w dół', hint: 'Delikatnie, wzrok cały czas na kropce.' },
}

type Rect = { x0: number; y0: number; w: number; h: number }

/** Where the stimulus (object-fit: contain, full-screen) actually lands, as a
 *  fraction of the viewport — mirrors StimulusScreen's letterboxing so the
 *  calibration dots sit on the real content area. */
function contentRect(aspect: number | null, vw: number, vh: number): Rect {
  if (!aspect || vw <= 0 || vh <= 0) return { x0: 0, y0: 0, w: 1, h: 1 }
  const vp = vw / vh
  if (aspect > vp) {
    // Stimulus wider than the viewport → fills width, letterboxed top/bottom.
    const h = vp / aspect
    return { x0: 0, y0: (1 - h) / 2, w: 1, h }
  }
  // Taller → fills height, pillarboxed left/right.
  const w = aspect / vp
  return { x0: (1 - w) / 2, y0: 0, w, h: 1 }
}

/**
 * Recorded multi-pose gaze calibration (eye-tracking studies), Z-45.
 *
 * Which points and how many head poses come from the study's
 * `calibration_variant` (chosen by the researcher in the wizard) via the
 * fixed shapes in `@/lib/calibrationVariants` — nothing here is fetched from
 * a gaze container (decision 2026-09-09).
 *
 * - Dots are placed inside the *stimulus content rectangle* (not the whole
 *   screen) and logged in content-normalised coords, so the server-side fit
 *   targets the same box the research overlay draws into.
 * - For each head pose in the variant, a short instruction screen asks the
 *   respondent to hold that pose, then the full point set for the variant
 *   runs again. Every collected point is tagged with its pose.
 * - On the "start" gesture we enter fullscreen and lock the orientation, so the
 *   browser chrome doesn't shrink the content and — critically — the respondent
 *   can't flip the phone mid-recording (which produced stretched, mis-parsed
 *   face clips). The whole recording stays in one orientation.
 * - Recording is started here (via ``onBegin``) AFTER fullscreen/lock settle, so
 *   the calibration frames and the stimulus share that single orientation.
 *
 * - Head pose is MEASURED, like in the admin lab (Z-119): MediaPipe Face
 *   Landmarker on the recording's own stream, a guidance screen per pose that
 *   waits until the head holds it, and a dot the head drifts out of is dropped
 *   and shown again — so every logged window is one the pose was really held
 *   for. No gaze model runs here; the gaze maths is server-side on the
 *   recorded frames. Without MediaPipe (load failure/timeout) the old timed
 *   run is used.
 *
 * NOTE: the mandatory validation pass documented in
 * warianty-kalibracji.md is NOT run here. It only produces a usable number
 * once the gaze container exists to score it (Z-40) and its error threshold
 * is still open (Z-14) — see FaceWise-brain/TODO.md Z-45.
 */
export default function EyeTrackingCalibration({
  study,
  streamRef,
  recordStartRef,
  onBegin,
  onComplete,
}: {
  study: RespondentStudy
  /** The camera stream from the tech check — also what is being recorded.
   *  Read here only to measure head pose; never stopped by this screen. */
  streamRef: React.MutableRefObject<MediaStream | null>
  /** Recording start (epoch ms); windows are logged relative to it, exactly
   *  like the stimulus markers, so they line up with AnalysisFrameResult.timestamp_ms. */
  recordStartRef: React.MutableRefObject<number>
  /** Start the webcam recording — called once, after fullscreen/lock settle and
   *  just before the first dot, so the whole clip is one orientation. */
  onBegin: () => void
  onComplete: (points: CalibrationPoint[]) => void
}) {
  const shape = useMemo(
    () => CALIBRATION_VARIANT_SHAPES[study.calibration_variant] ?? CALIBRATION_VARIANT_SHAPES[DEFAULT_CALIBRATION_VARIANT],
    [study.calibration_variant],
  )
  // The run has two stages: calibration (every point at every head pose) and a
  // closing validation pass, always straight-on. Same dot, same dwell — only
  // the label on the data differs, which is what lets the engine score itself.
  const [stage, setStage] = useState<'calibration' | 'validation'>('calibration')
  const targets = stage === 'calibration' ? shape.points : shape.validationTargets
  const poses: HeadPose[] = stage === 'calibration' ? shape.poses : ['center']

  // 'pose' = waiting for the head to hold this pose (guided), or a timed
  // instruction screen when the tracker is unavailable.
  const [phase, setPhaseState] = useState<'intro' | 'pose' | 'running'>('intro')
  const [poseIdx, setPoseIdx] = useState(0)
  const [index, setIndex] = useState(0)
  const [aspect, setAspect] = useState<number | null>(null)
  const [aspectReady, setAspectReady] = useState(false)
  const [viewport, setViewport] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 0,
    h: typeof window !== 'undefined' ? window.innerHeight : 0,
  }))
  const collected = useRef<CalibrationPoint[]>([])

  // --- head-pose guidance (refs: read by the per-frame handler) ---
  const videoRef = useRef<HTMLVideoElement>(null)
  const [tracker, setTracker] = useState<'loading' | 'ready' | 'off'>('loading')
  const guided = tracker === 'ready'
  const phaseRef = useRef<'intro' | 'pose' | 'running'>('intro')
  const poseRef = useRef<HeadPose>('center')
  const baselineRef = useRef<HeadDeg | null>(null)
  const holdRef = useRef(new PoseHold())
  // False after "continue without guidance" — the rest of that pose runs timed.
  const guardRef = useRef(true)
  const faceLostSince = useRef<number | null>(null)
  const lastHeadRef = useRef<HeadDeg | null>(null)
  const lastViewT = useRef(0)
  const [poseView, setPoseView] = useState<PoseView>(EMPTY_POSE_VIEW)
  const [canSkip, setCanSkip] = useState(false)
  const driftsRef = useRef(0)

  const setPhase = useCallback((next: 'intro' | 'pose' | 'running') => {
    phaseRef.current = next
    if (next === 'pose') {
      holdRef.current.reset()
      lastViewT.current = 0
      setPoseView({ ...EMPTY_POSE_VIEW, establishing: baselineRef.current === null })
    }
    faceLostSince.current = null
    setPhaseState(next)
  }, [])

  const elapsed = () => (recordStartRef.current ? Date.now() - recordStartRef.current : 0)

  // Track viewport so the content rect (and rotation state) follow orientation changes.
  useEffect(() => {
    const onResize = () => setViewport({ w: window.innerWidth, h: window.innerHeight })
    window.addEventListener('resize', onResize)
    window.addEventListener('orientationchange', onResize)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('orientationchange', onResize)
    }
  }, [])

  // Measure the first visual stimulus's aspect ratio (w/h) so the dots and the
  // rotation hint match how it'll be shown. Falls back to 16:9 for video/url.
  useEffect(() => {
    const visual = study.stimuli.find(
      (s) => (s.type === 'video' || s.type === 'image' || s.type === 'url') && (s.url || s.payload),
    )
    if (!visual) {
      setAspect(null)
      setAspectReady(true)
      return
    }
    let done = false
    const finish = (a: number | null) => {
      if (done) return
      done = true
      setAspect(a && isFinite(a) && a > 0 ? a : null)
      setAspectReady(true)
    }
    const timer = window.setTimeout(
      () => finish(visual.type === 'image' ? null : DEFAULT_VIDEO_ASPECT),
      ASPECT_TIMEOUT_MS,
    )
    if (visual.type === 'url' && youTubeId(visual.url)) {
      window.clearTimeout(timer)
      finish(DEFAULT_VIDEO_ASPECT)
    } else if (visual.type === 'image') {
      const img = new Image()
      img.onload = () => {
        window.clearTimeout(timer)
        finish(img.naturalWidth / img.naturalHeight)
      }
      img.onerror = () => {
        window.clearTimeout(timer)
        finish(null)
      }
      img.src = visual.url
    } else {
      const v = document.createElement('video')
      v.preload = 'metadata'
      v.muted = true
      v.onloadedmetadata = () => {
        window.clearTimeout(timer)
        finish(v.videoWidth && v.videoHeight ? v.videoWidth / v.videoHeight : DEFAULT_VIDEO_ASPECT)
      }
      v.onerror = () => {
        window.clearTimeout(timer)
        finish(DEFAULT_VIDEO_ASPECT)
      }
      v.src = visual.url
    }
    return () => window.clearTimeout(timer)
  }, [study])

  // Read by the frame handler; plain render-time mirror of the current pose.
  poseRef.current = poses[poseIdx] ?? 'center'

  // A new pose (or the validation pass) starts with fresh guidance.
  useEffect(() => {
    guardRef.current = true
    driftsRef.current = 0
  }, [poseIdx, stage])

  // Head tracking for the guidance. Shares the recording's stream through its
  // own hidden <video>; disposing it never touches the stream.
  useEffect(() => {
    const video = videoRef.current
    const stream = streamRef.current
    if (!video || !stream) {
      setTracker('off')
      return
    }
    let cancelled = false
    const t = new GazeTracker()
    video.srcObject = stream
    void video.play().catch(() => {})
    const timeout = window.setTimeout(() => {
      if (!cancelled) setTracker((s) => (s === 'loading' ? 'off' : s))
    }, TRACKER_LOAD_TIMEOUT_MS)
    t.init(video)
      .then(() => {
        if (cancelled) return
        window.clearTimeout(timeout)
        t.onFrame = (frame) => handleFrameRef.current(frame)
        t.start()
        // Timed out first → stay unguided; flipping mid-run would change the rules.
        setTracker((s) => (s === 'loading' ? 'ready' : s))
      })
      .catch(() => {
        if (!cancelled) setTracker('off')
      })
    return () => {
      cancelled = true
      window.clearTimeout(timeout)
      t.dispose()
      video.srcObject = null
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleFrame = (frame: GazeFrame) => {
    const ph = phaseRef.current
    const head = frame.faceOk && frame.features ? headFromBase(frame.features.base) : null
    if (head) lastHeadRef.current = head
    if (ph === 'pose') {
      if (!guardRef.current) return
      const res = holdRef.current.update(head, baselineRef.current, poseRef.current, frame.t)
      if (frame.t - lastViewT.current > 66 || res.done) {
        lastViewT.current = frame.t
        setPoseView(res.view)
      }
      if (res.done) {
        baselineRef.current = res.baseline
        setPhase('running')
      }
      return
    }
    if (ph !== 'running' || !guardRef.current) return
    // Head out of the pose (or out of frame) mid-dot: drop this dot, re-acquire,
    // then show the same dot again — the logged window must be a clean one.
    if (head) {
      faceLostSince.current = null
      if (!within(poseOffset(head, baselineRef.current, poseRef.current), POSE_TOLERANCE_DEG)) {
        driftsRef.current += 1
        setPhase('pose')
      }
    } else {
      if (faceLostSince.current === null) faceLostSince.current = frame.t
      else if (frame.t - faceLostSince.current > FACE_LOST_MS) {
        driftsRef.current += 1
        setPhase('pose')
      }
    }
  }
  const handleFrameRef = useRef(handleFrame)
  handleFrameRef.current = handleFrame

  // Pose screen. Unguided: hold the instruction for POSE_INTRO_MS, then run.
  // Guided: the frame handler moves on; offer an escape if it takes too long.
  useEffect(() => {
    if (phase !== 'pose') return
    if (!guided || !guardRef.current) {
      const timer = window.setTimeout(() => setPhase('running'), POSE_INTRO_MS)
      return () => window.clearTimeout(timer)
    }
    setCanSkip(driftsRef.current >= SKIP_AFTER_DRIFTS)
    const timer = window.setTimeout(() => setCanSkip(true), SKIP_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [phase, poseIdx, stage, guided, setPhase])

  /** "Continue without guidance": finish this pose timed, as before Z-119. */
  const skipGuidance = useCallback(() => {
    guardRef.current = false
    if (baselineRef.current === null) baselineRef.current = lastHeadRef.current ?? { yaw: 0, pitch: 0 }
    setPhase('running')
  }, [setPhase])

  // Drive the dot sequence for the current pose: log each target's full
  // shown->hidden window (no client-side settle trim — see POINT_MS above),
  // then either move to the next dot, the next pose, or finish.
  useEffect(() => {
    if (phase !== 'running') return
    if (index >= targets.length) {
      if (poseIdx + 1 < poses.length) {
        setPoseIdx((p) => p + 1)
        setIndex(0)
        setPhase('pose')
      } else if (stage === 'calibration' && shape.validationTargets.length > 0) {
        setStage('validation')
        setPoseIdx(0)
        setIndex(0)
        setPhase('pose')
      } else {
        onComplete(collected.current)
      }
      return
    }
    let cancelled = false
    const start_ms = elapsed()
    // Snapshot the rectangle this dot is actually drawn in, so the viewport
    // coordinates logged below match the pixels the respondent looked at even
    // if the viewport changes between dots.
    const rect = contentRect(aspect, viewport.w, viewport.h)
    const point = targets[index]
    const timer = window.setTimeout(() => {
      if (cancelled) return
      collected.current.push({
        ...point,
        viewport_x: rect.x0 + point.x * rect.w,
        viewport_y: rect.y0 + point.y * rect.h,
        start_ms,
        end_ms: elapsed(),
        pose: poses[poseIdx],
        stage,
      })
      setIndex((i) => i + 1)
    }, POINT_MS)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, index, poseIdx, stage])

  const begin = useCallback(() => {
    // In the gesture: maximise the content and freeze the orientation so the
    // recording stays consistent, THEN start recording, THEN show the dots.
    void enterFullscreen().then(lockOrientationToCurrent)
    collected.current = []
    baselineRef.current = null
    setStage('calibration')
    setPoseIdx(0)
    setIndex(0)
    onBegin()
    setPhase('pose')
  }, [onBegin, setPhase])

  // Hidden camera preview the head tracker reads; kept mounted across phases.
  const video = (
    <video
      ref={videoRef}
      muted
      playsInline
      style={{ position: 'fixed', width: 2, height: 2, opacity: 0, pointerEvents: 'none', left: 0, top: 0 }}
    />
  )
  return (
    <>
      {video}
      {renderPhase()}
    </>
  )

  function renderPhase() {
  if (phase === 'intro') {
    return (
      <Box sx={{ minHeight: '100dvh', bgcolor: C.navy900, display: 'grid', placeItems: 'center', px: 3 }}>
        <Stack spacing={3} sx={{ maxWidth: 520, textAlign: 'center', alignItems: 'center' }}>
          <Typography sx={{ fontSize: 12, letterSpacing: '.1em', textTransform: 'uppercase', color: C.coral, fontWeight: 700 }}>
            Kalibracja wzroku
          </Typography>
          <Typography variant="h4" sx={{ color: '#fff', fontSize: { xs: 24, md: 32 } }}>
            Powiódź wzrokiem za kropką
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,.7)', fontSize: 15 }}>
            Za chwilę pojawią się kropki w różnych miejscach ekranu
            {poses.length > 1 ? ', w kilku ustawieniach głowy' : ''}. Trzymaj komputer/ekran
            nieruchomo i patrz na każdą kropkę, aż zniknie.
            {guided ? ' Przed każdą serią pokażemy, jak ustawić głowę — kropki ruszą, gdy ją utrzymasz.' : ''}{' '}
            Ekran przejdzie w tryb pełnoekranowy. Zajmie to około {Math.round(shape.approxSeconds)} sekund.
          </Typography>
          <Button
            onClick={begin}
            disabled={!aspectReady || tracker === 'loading'}
            variant="contained"
            sx={{ bgcolor: C.coral, '&:hover': { bgcolor: C.coral }, px: 4, py: 1.25, borderRadius: 2, fontWeight: 700 }}
          >
            Rozpocznij kalibrację
          </Button>
        </Stack>
      </Box>
    )
  }

  if (phase === 'pose' && guided && guardRef.current) {
    const pose = poses[poseIdx]
    const copy = stage === 'validation' ? VALIDATION_COPY : POSE_COPY[pose]
    return (
      <Box sx={{ position: 'fixed', inset: 0, bgcolor: C.navy900, zIndex: 1300 }}>
        <PoseGuide
          view={poseView}
          title={copy.title}
          hint={copy.hint}
          counter={stage === 'calibration' && poses.length > 1 ? `Ustawienie głowy ${poseIdx + 1} / ${poses.length}` : null}
          accent={C.coral}
          onSkip={canSkip ? skipGuidance : undefined}
          skipLabel="Kontynuuj bez ustawiania głowy"
        />
      </Box>
    )
  }

  if (phase === 'pose') {
    const pose = poses[poseIdx]
    const copy = stage === 'validation' ? VALIDATION_COPY : POSE_COPY[pose]
    return (
      <Box sx={{ position: 'fixed', inset: 0, bgcolor: C.navy900, display: 'grid', placeItems: 'center', zIndex: 1300, px: 3 }}>
        <Stack spacing={1.5} sx={{ maxWidth: 480, textAlign: 'center', alignItems: 'center' }}>
          {poses.length > 1 && (
            <Typography sx={{ fontSize: 12, letterSpacing: '.08em', color: 'rgba(255,255,255,.5)' }}>
              Ustawienie głowy {poseIdx + 1} / {poses.length}
            </Typography>
          )}
          <Typography variant="h5" sx={{ color: '#fff', fontSize: { xs: 20, md: 26 } }}>
            {copy.title}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,.7)', fontSize: 14 }}>{copy.hint}</Typography>
        </Stack>
      </Box>
    )
  }

  // running — place the dot inside the stimulus content rectangle.
  const rect = contentRect(aspect, viewport.w, viewport.h)
  const target = targets[Math.min(index, targets.length - 1)]
  const left = (rect.x0 + target.x * rect.w) * 100
  const top = (rect.y0 + target.y * rect.h) * 100
  return (
    <Box sx={{ position: 'fixed', inset: 0, bgcolor: C.navy900, overflow: 'hidden', zIndex: 1300 }}>
      {/* Faint outline of the actual content area, so the respondent (and we)
          can see the dots track the stimulus rectangle, not the whole screen. */}
      {aspect != null && (rect.w < 0.98 || rect.h < 0.98) && (
        <Box
          sx={{
            position: 'absolute',
            left: `${rect.x0 * 100}%`,
            top: `${rect.y0 * 100}%`,
            width: `${rect.w * 100}%`,
            height: `${rect.h * 100}%`,
            border: '1px dashed rgba(255,255,255,.18)',
            borderRadius: 1,
          }}
        />
      )}
      <Typography
        sx={{
          position: 'absolute',
          top: 16,
          left: 0,
          right: 0,
          textAlign: 'center',
          color: 'rgba(255,255,255,.5)',
          fontSize: 13,
          letterSpacing: '.08em',
        }}
      >
        Patrz na kropkę · {Math.min(index + 1, targets.length)} / {targets.length}
        {poses.length > 1 ? ` · poza ${poseIdx + 1}/${poses.length}` : ''}
      </Typography>
      <Box
        sx={{
          position: 'absolute',
          left: `${left}%`,
          top: `${top}%`,
          transform: 'translate(-50%, -50%)',
          // Smooth glide between dots; the full window (incl. this glide) is
          // now what gets logged — see POINT_MS above.
          transition: 'left .4s ease, top .4s ease',
          width: 28,
          height: 28,
          borderRadius: '50%',
          bgcolor: C.coral,
          boxShadow: `0 0 0 6px rgba(255,255,255,.15), 0 0 22px ${C.coral}`,
          '@keyframes calPulse': {
            '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)' },
            '50%': { transform: 'translate(-50%, -50%) scale(0.6)' },
          },
          animation: 'calPulse 1.2s ease-in-out infinite',
        }}
      />
    </Box>
  )
  }
}
